import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterModule } from '@angular/router';
import currency from 'currency.js';
import Currency from 'currency.js';
import { NgxMaskModule } from 'ngx-mask';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';

import { IPartner } from '../../../../pages/dashboard/models/partner';
import { IPayrollSettingsList } from '../../../../pages/dashboard/models/payroll-settings-list.interface';
import { PayrollSettingsTypeEnum } from '../../../../pages/dashboard/models/payroll-settings-type.enum';
import { PayrollSettingsService } from '../../../../pages/dashboard/services/payroll-settings.service';
import { ThemesService } from '../../../../services/themes.service';
import { IExternalDealDetailsDTO } from '../../../advance-application/models/external-deal.interface';
import { DealService } from '../../../advance-application/services/deal.service';
import { AstraConnectionComponent } from '../../../astra/components/astra-connection/astra-connection.component';
import { AstraUserCardDTO } from '../../../astra/models/astra-user-cards.dto';
import { AstraService } from '../../../astra/services/astra.service';
import { AstraUserStatusEnum } from '../../../authentication/models/astra-user-status.enum';
import { V1IBankAccount } from '../../../authentication/models/v1-api.interface';
import { AuthenticationService } from '../../../authentication/services/authentication.service';
import { IIngoCard } from '../../../ingo/models/ingo-card.interface';
import { IngoService } from '../../../ingo/services/ingo.service';
import { BankInfoPipe } from '../../../pipes/bank-info.pipe';
import { CurrencyJsPipe } from '../../../pipes/currency-js.pipe';
import { DatePipe } from '../../../pipes/date.pipe';
import { LocalisePipe } from '../../../pipes/localise.pipe';
import { DisableAutofillDirective } from '../../../shared/directives/disable-autofill.directive';
import { BalanceByDeal } from '../../../shared/models/balance.interface';
import { FormatDateEnum } from '../../../shared/models/format-date.enum';
import { AccountService } from '../../../shared/services/account.service';
import { BankService } from '../../../shared/services/bank.service';
import { BrokerService } from '../../../shared/services/broker.service';
import { SharedService } from '../../../shared/services/shared.service';

@Component({
  selector: 'app-confirm-deposit-distributions',
  templateUrl: './confirm-deposit-distributions.component.html',
  styleUrls: ['./confirm-deposit-distributions.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatDividerModule,
    MatButtonModule,
    LocalisePipe,
    CurrencyJsPipe,
    DatePipe,
    MatIcon,
    FormsModule,
    DisableAutofillDirective,
    NgxMaskModule,
  ],
})
export class ConfirmDepositDistributionsComponent
  extends AstraConnectionComponent
  implements OnInit
{
  public static readonly DIALOG_CONFIG = {
    minHeight: 300,
  };

  public externalDealId: number;
  public loading = true;
  public saving = false;
  public list: IPayrollSettingsList[] = [];
  public ingoCards: IIngoCard[] = [];
  public ingoCardsMap: Record<number, IIngoCard> = {};
  public editPercentIngoCardsMap: Record<number | string, number> = {};
  public bankAccounts?: V1IBankAccount[];
  public bankAccountsMap?: Record<number, V1IBankAccount>;
  public astraCards: AstraUserCardDTO[] = [];
  public astraCardsMap: Record<string, AstraUserCardDTO> = {};
  public totalPercent: Currency = new Currency(100);
  public equalPercent = true;
  public balanceByDeal: BalanceByDeal;
  public editPercentBankAccountsMap: Record<number | string, number> = {};
  public percentBrokerBankMap: Record<number | string, Currency>;
  public editPercentBrokerBankMap: Record<number | string, number> = {};
  public instantFeePercent = Currency(this.defaultInstantFeePercent);
  public externalFeePercent = 0.5;
  public formatDateEnum = FormatDateEnum;
  public details: IExternalDealDetailsDTO;
  public editPayrollList: IPayrollSettingsList[];
  public totalToDeposit = currency(0);
  public partnerList?: IPartner[];

  public showTooltipStandard = false;
  public showTooltipInstant = false;

  constructor(
    public toastrService: ToastrService,
    public sharedService: SharedService,
    public authenticationService: AuthenticationService,
    public dialog: MatDialog,
    public themesService: ThemesService,
    public astraService: AstraService,
    private ingoService: IngoService,
    private bankService: BankService,
    private payrollSettingsService: PayrollSettingsService,
    public dealService: DealService,
    public dialogRef: MatDialogRef<ConfirmDepositDistributionsComponent>,
    private accountService: AccountService,
    public brokerService: BrokerService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadAll();
  }

  private async loadAll(): Promise<void> {
    this.loading = true;
    try {
      await Promise.all([
        this.load(),
        this.loadBankAccounts(),
        this.loadAstra(),
        this.loadIngoCards(),
        firstValueFrom(this.astraService.getWithdrawalInfo()).then(
          (info) => (this.instantFeePercent = Currency(info.instantFeePercent)),
        ),
      ]);
    } catch (e) {
      this.toastrService.error(this.sharedService.getErrorText(e, true));
    }

    this.editPayrollList = this.list.map((item) => ({ ...item }));

    this.bankAccounts?.forEach((bankAccount) => {
      const existsInList = this.editPayrollList.some(
        (item) => item.bankAccount?.id === bankAccount.id,
      );

      if (!existsInList) {
        const newPayrollSetting: IPayrollSettingsList = {
          bankAccount,
          bankAccountId: bankAccount.id,
          percent: new Currency(0),
          type: PayrollSettingsTypeEnum.BankAccount,
          active: false,
          createdAt: new Date(),
          externalDealId: this.externalDealId,
        };

        this.editPayrollList.push(newPayrollSetting);
      }
    });

    this.astraCards.forEach((card) => {
      const existsInList = this.editPayrollList.some(
        (item) => item.externalCardId === card.id,
      );

      if (!existsInList) {
        const newPayrollSetting: IPayrollSettingsList = {
          percent: new Currency(0),
          type: PayrollSettingsTypeEnum.AstraDebitCard,
          active: false,
          createdAt: new Date(),
          externalCardId: card.id,
          externalDealId: this.externalDealId,
        };

        this.editPayrollList.push(newPayrollSetting);
      }
    });

    this.calcPercent();
    this.loading = false;
  }

  public async loadIngoCards(): Promise<void> {
    try {
      this.ingoCards = (await this.ingoService.getCards().toPromise()) || [];
      this.ingoCardsMap = {};
      this.ingoCards.forEach((card) => {
        this.ingoCardsMap[card.id] = card;
      });
    } catch (e) {
      this.toastrService.error(this.sharedService.getErrorText(e, true));
    }
  }

  private async load(): Promise<void> {
    try {
      [this.details, this.list, this.partnerList] = await Promise.all([
        firstValueFrom(this.dealService.getExternalDeal(this.externalDealId)),
        firstValueFrom(this.payrollSettingsService.get()),
        firstValueFrom(this.brokerService.getPartnersList()),
      ]);
      if (this.details.payrollToClocDealLinkId) {
        const balanceByDeals = await firstValueFrom(
          this.accountService.getBalanceByDeals(),
        );

        const balanceByDeal =
          balanceByDeals[this.details.payrollToClocDealLinkId];
        if (balanceByDeal) {
          this.balanceByDeal = balanceByDeal;
        }
      }
      this.editPercentBankAccountsMap = {};
      for (const payroll of this.list) {
        if (payroll.bankAccountId) {
          this.editPercentBankAccountsMap[payroll.bankAccountId] =
            payroll.percent.value;
        } else if (payroll.externalCardId) {
          this.editPercentBankAccountsMap[payroll.externalCardId] =
            payroll.percent.value;
        } else if (payroll.ingoCardId) {
          this.editPercentIngoCardsMap[payroll.ingoCardId] =
            payroll.percent.value;
        } else if (payroll.brokerBankAccountId) {
          this.editPercentBrokerBankMap[payroll.brokerBankAccountId] =
            payroll.percent.value;
        }
      }

      this.totalToDeposit = this.details.agentCommissionAmount.subtract(
        this.balanceByDeal?.unpaidTotal || this.details.paymentFromPartner,
      );
    } catch (e) {
      this.toastrService.error(this.sharedService.getErrorText(e, true));
    }
  }

  private async loadBankAccounts(): Promise<void> {
    this.bankAccounts = await firstValueFrom(this.bankService.getList());
    this.bankAccounts.forEach((bankAccount) => {
      bankAccount.accountNumber = bankAccount.accountNumber.slice(-10);
    });
    this.bankAccountsMap = {};
    for (const account of this.bankAccounts) {
      this.bankAccountsMap[account.id] = account;
    }
  }

  private async loadAstra(): Promise<void> {
    const user = this.authenticationService.getProfileState();
    if (
      user.astraIsConnected &&
      user.astraUserStatus === AstraUserStatusEnum.Approved
    ) {
      await this.loadAstraCards({});
      if (this.astraCards) {
        this.astraCards.forEach((card) => {
          for (const item of this.list) {
            if (item.externalCardId === card.id) {
              this.editPercentBankAccountsMap[card.id] = item.percent.value;
              break;
            }
          }
        });
      }
    }
  }

  public changePercent(item: IPayrollSettingsList, value: number): void {
    item.percent = currency(value);
    this.calcPercent();
  }

  public calcPercent(): void {
    this.saving = true;
    this.totalPercent = Currency(0);
    for (const key in this.editPercentBankAccountsMap) {
      this.totalPercent = this.totalPercent.add(
        Currency(this.editPercentBankAccountsMap[key]),
      );
    }
    for (const key in this.editPercentIngoCardsMap) {
      this.totalPercent = this.totalPercent.add(
        Currency(this.editPercentIngoCardsMap[key]),
      );
    }

    for (const key in this.editPercentBrokerBankMap) {
      this.totalPercent = this.totalPercent.add(
        Currency(this.editPercentBrokerBankMap[key]),
      );
    }

    this.equalPercent = this.totalPercent.value === 100;
    this.saving = false;
  }

  public async confirmDeposit(): Promise<void> {
    this.calcPercent();
    if (!this.equalPercent) {
      return;
    }

    this.saving = true;

    const updateList: IPayrollSettingsList[] = [];

    if (this.bankAccounts) {
      this.bankAccounts.forEach((item) => {
        if (this.editPercentBankAccountsMap[item.id] > 0) {
          const newItem: IPayrollSettingsList = {
            percent: Currency(this.editPercentBankAccountsMap[item.id]),
            type: PayrollSettingsTypeEnum.BankAccount,
            bankAccountId: item.id,
            bankAccount: item,
            active: true,
            externalDealId: this.externalDealId,
          };
          updateList.push(newItem);
        }
      });
    }

    if (this.astraCards) {
      this.astraCards.forEach((item) => {
        if (this.editPercentBankAccountsMap[item.id] > 0) {
          const newItem: IPayrollSettingsList = {
            externalCardId: item.id,
            percent: Currency(this.editPercentBankAccountsMap[item.id]),
            type: PayrollSettingsTypeEnum.AstraDebitCard,
            active: true,
            externalDealId: this.externalDealId,
          };
          updateList.push(newItem);
        }
      });
    }

    this.ingoCards?.forEach((item) => {
      if (this.editPercentIngoCardsMap[item.id] > 0) {
        const newItem: IPayrollSettingsList = {
          ingoCardId: item.id,
          percent: Currency(this.editPercentIngoCardsMap[item.id]),
          type: PayrollSettingsTypeEnum.IngoCard,
          active: true,
          externalDealId: this.externalDealId,
        };
        updateList.push(newItem);
      }
    });

    this.partnerList?.forEach((item) => {
      item.brokerBankAccount.forEach((bank) => {
        if (this.editPercentBrokerBankMap[bank.id] > 0) {
          const newItem: IPayrollSettingsList = {
            brokerBankAccountId: bank.id,
            percent: Currency(this.editPercentBrokerBankMap[bank.id]),
            type: PayrollSettingsTypeEnum.ExternalBrokerAccount,
            active: true,
          };
          updateList.push(newItem);
        }
      });
    });

    try {
      await this.payrollSettingsService
        .update({ list: updateList }, { externalDealId: this.externalDealId })
        .toPromise();
      this.toastrService.success('Success!');
      this.dialogRef.close(true);
    } catch (e) {
      this.toastrService.error(this.sharedService.getErrorText(e, true));
    }
    this.saving = false;
  }

  public changeActive(payroll: IPayrollSettingsList): void {
    payroll.active = !payroll.active;
    if (!payroll.active) {
      payroll.percent = new Currency(0);
    }
    this.calcPercent();
  }

  public showTooltip(): void {
    this.showTooltipStandard = !this.showTooltipStandard;
  }
  public showHideTooltipInstant(): void {
    this.showTooltipInstant = !this.showTooltipInstant;
  }
}
